import axios from 'axios';
import { env } from '@/config/env';
import { useAuthStore } from '../store/authStore';
import { tokenRequest } from '@/config/auth';
import { logToken } from '../utils/jwt';

const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000,
});

// Request interceptor to add authentication token
apiClient.interceptors.request.use(
  async (config) => {
    const { msalInstance, isAuthenticated } = useAuthStore.getState();

    // Only proceed if user is authenticated
    if (!isAuthenticated || !msalInstance) {
      console.warn('User not authenticated. Request will be sent without token.');
      return config;
    }

    try {
      // Prefer active account, fallback to first cached account
      const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

      if (!account) {
        console.warn('No accounts found in MSAL cache.');
        return config;
      }

      // Try to acquire token silently (from cache or refresh)
      try {
        const response = await msalInstance.acquireTokenSilent({
          ...tokenRequest,
          account,
        });

        // LOG TOKEN: Before using in API request
        logToken(response.accessToken, 'API REQUEST');

        // Add token to Authorization header
        config.headers.Authorization = `Bearer ${response.accessToken}`;
        console.debug('Token acquired successfully:', {
          scopes: response.scopes,
          expiresOn: response.expiresOn,
        });

        return config;
      } catch (silentError) {
        console.warn('Silent token acquisition failed, trying interactive flow:', silentError);

        // If silent acquisition fails, try interactive popup
        try {
          const response = await msalInstance.acquireTokenPopup({
            ...tokenRequest,
            account,
          });

          // LOG TOKEN: Acquired via popup (interactive)
          logToken(response.accessToken, 'API REQUEST (POPUP)');

          config.headers.Authorization = `Bearer ${response.accessToken}`;
          console.info('Token acquired via popup successfully');

          return config;
        } catch (popupError) {
          console.error('Interactive token acquisition failed:', popupError);
          throw popupError;
        }
      }
    } catch (error) {
      console.error('Failed to acquire token:', error);

      // If token acquisition fails completely, logout user
      const { logout } = useAuthStore.getState();
      logout();

      throw new Error('Authentication failed. Please log in again.');
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error)) {
      const apiError = error.response?.data?.error;
      const message = apiError?.message || error.message || 'API request failed';

      // Handle 401 Unauthorized
      if (error.response?.status === 401) {
        const { logout, setError, isAuthenticated } = useAuthStore.getState();

        if (message.toLowerCase().includes('unauthorized tenant')) {
          // Tenant not in backend allowlist - show clear message and logout
          console.warn('[Auth] Tenant not authorized by backend:', message);
          setError(
            'Your organization is not authorized to use this application. Please contact the administrator.'
          );
          if (isAuthenticated) {
            logout();
          }
        } else if (isAuthenticated) {
          // Other 401 errors - token expired or invalid
          console.warn('[Auth] Received 401 - logging out user');
          logout();
        }
      }

      throw new Error(message);
    }
    throw error;
  }
);

export default apiClient;
